import { ReactNode, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Accordion, Badge, Button, Card, Col, Form, Row } from 'react-bootstrap';

import {
  GachaAppVersionOperator,
  GetSpendTierAudienceQuery,
  GetSpendTierAudienceQueryVariables,
  UserSpendTier,
  UserSubscriptionTier,
} from '../../../../../../__gqltypes__';
import { SPEND_TIER_AUDIENCE_QUERY } from '../../SaleEvent/graphql';
import {
  describeGachaMatcherFilters,
  GACHA_SPEND_TIER_OPTIONS,
  GACHA_VERSION_OPERATOR_LABELS,
  GachaMatcherDraft,
  gachaMatcherLevel,
  gachaUserIdList,
} from '../utils';
import { GachaUtcDateTime } from './GachaUtcDateTime';

type Props = {
  // Stable id prefix for the form controls.
  idPrefix: string;
  matcher: GachaMatcherDraft;
  onChange?: (matcher: GachaMatcherDraft) => void;
  readOnly?: boolean;
};

const ALL_SUBSCRIPTION_TIERS = 'All';

/**
 * The Gacha matcher: Sale Event's criteria, in its form order, with three differences. The build version keeps its
 * comparison operator (Sale Event offers a minimum only), Only Maestro is its own row below the spend tier, and
 * "Has purchased subscription before" is not part of it (not relevant to Gacha Shop). Every
 * criterion is ANDed; Excluded User Ids always wins. The namespace is a campaign field, edited next to the window.
 */
export function GachaMatcherFields({ idPrefix, matcher, onChange, readOnly = false }: Props): JSX.Element {
  const update = (patch: Partial<GachaMatcherDraft>) => onChange?.({ ...matcher, ...patch });
  const disabled = readOnly || !onChange;

  // Sale Event's estimate, reused as it is: it takes the criteria as an argument and is not bound to a sale event.
  // An explicit action, as there: the count scans every payer row.
  const [loadAudience, { data: audienceData, loading: audienceLoading }] = useLazyQuery<
    GetSpendTierAudienceQuery,
    GetSpendTierAudienceQueryVariables
  >(SPEND_TIER_AUDIENCE_QUERY, { fetchPolicy: 'no-cache' });
  const audienceByTier = useMemo(() => {
    const rows = audienceData?.liveOps.spendTierAudience ?? [];
    return rows.reduce<Record<string, typeof rows[number]>>((acc, row) => ({ ...acc, [row.tier]: row }), {});
  }, [audienceData]);
  const audienceComputedAt = audienceData?.liveOps.spendTierAudience?.find((row) => row.computedAt)?.computedAt;
  // The estimate has no offline equivalent for these: the real audience is smaller than the count when any is set.
  const audienceIgnored = [
    matcher.appVersion.trim() ? 'build version' : undefined,
    matcher.subscriptionTier ? 'subscription status' : undefined,
    matcher.maestroOnly ? 'Only Maestro' : undefined,
  ].filter((name): name is string => !!name);
  const level = gachaMatcherLevel(matcher);

  const estimate = () =>
    loadAudience({
      variables: {
        filters: {
          userIds: gachaUserIdList(matcher.userIds),
          excludedUserIds: gachaUserIdList(matcher.excludedUserIds),
          userLevel: level != null && Number.isInteger(level) ? level : undefined,
          userMinCreationDate: matcher.userMinCreationDate ?? undefined,
          userMaxCreationDate: matcher.userMaxCreationDate ?? undefined,
        },
      },
    });

  const toggleSpendTier = (tier: UserSpendTier) =>
    update({
      spendTiers: matcher.spendTiers.includes(tier)
        ? matcher.spendTiers.filter((selected) => selected !== tier)
        : [...matcher.spendTiers, tier],
    });
  const allSpendTiersSelected = matcher.spendTiers.length === GACHA_SPEND_TIER_OPTIONS.length;

  type DateKey = 'userMinCreationDate' | 'userMaxCreationDate';
  const setDate = (key: DateKey, value: number | null) =>
    update(key === 'userMinCreationDate' ? { userMinCreationDate: value } : { userMaxCreationDate: value });

  const dateRow = (key: DateKey, label: string) => (
    <Form.Group as={Row} className="mb-2">
      <Form.Label column md={3} htmlFor={`${idPrefix}-${key}`}>
        {label}
      </Form.Label>
      <Col md={4}>
        <GachaUtcDateTime
          inputId={`${idPrefix}-${key}`}
          value={matcher[key]}
          disabled={disabled}
          onChange={(value) => setDate(key, value)}
        />
      </Col>
      <Col md={5}>
        {matcher[key] != null && !disabled ? (
          <Button variant="link" size="sm" className="p-0" onClick={() => setDate(key, null)}>
            Clear
          </Button>
        ) : (
          <Form.Text className="text-muted">UTC. Empty = no bound.</Form.Text>
        )}
      </Col>
    </Form.Group>
  );

  return (
    <>
      {dateRow('userMinCreationDate', 'Minimum Registration Date')}
      {dateRow('userMaxCreationDate', 'Maximum Registration Date')}
      <Form.Group as={Row} className="mb-2">
        <Form.Label column md={3} htmlFor={`${idPrefix}-version`}>
          Build version
        </Form.Label>
        <Col md={2}>
          <Form.Control
            as="select"
            aria-label="Build version operator"
            value={matcher.appVersionOperator}
            disabled={disabled}
            onChange={(e) => update({ appVersionOperator: e.target.value as GachaAppVersionOperator })}
          >
            {Object.values(GachaAppVersionOperator).map((operator) => (
              <option key={operator} value={operator}>
                {GACHA_VERSION_OPERATOR_LABELS[operator]}
              </option>
            ))}
          </Form.Control>
        </Col>
        <Col md={3}>
          <Form.Control
            id={`${idPrefix}-version`}
            placeholder="e.g. 003.020.000"
            value={matcher.appVersion}
            disabled={disabled}
            onChange={(e) => update({ appVersion: e.target.value })}
          />
        </Col>
        <Col md={4}>
          <Form.Text className="text-muted">Empty = any build.</Form.Text>
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column md={3} htmlFor={`${idPrefix}-level`}>
          Min User Level
        </Form.Label>
        <Col md={2}>
          <Form.Control
            id={`${idPrefix}-level`}
            value={matcher.userLevel}
            disabled={disabled}
            onChange={(e) => update({ userLevel: e.target.value })}
          />
        </Col>
        <Col md={7}>
          <Form.Text className="text-muted">Empty = any level.</Form.Text>
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column md={3} htmlFor={`${idPrefix}-subscription`}>
          User Subscription Status
        </Form.Label>
        <Col md={3}>
          <Form.Control
            as="select"
            id={`${idPrefix}-subscription`}
            value={matcher.subscriptionTier ?? ALL_SUBSCRIPTION_TIERS}
            disabled={disabled}
            onChange={(e) =>
              update({
                subscriptionTier:
                  e.target.value === ALL_SUBSCRIPTION_TIERS ? null : (e.target.value as UserSubscriptionTier),
              })
            }
          >
            <option value={ALL_SUBSCRIPTION_TIERS}>All</option>
            {Object.values(UserSubscriptionTier).map((tier) => (
              <option key={tier} value={tier}>
                {tier}
              </option>
            ))}
          </Form.Control>
        </Col>
        <Col md={6}>
          <Form.Text className="text-muted">One tier exactly: BASIC reaches non-subscribers only.</Form.Text>
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column md={3} htmlFor={`${idPrefix}-user-ids`}>
          User Ids
          <br />
          <small className="text-muted">(separated by commas &quot;,&quot;)</small>
        </Form.Label>
        <Col md={8}>
          <Form.Control
            as="textarea"
            id={`${idPrefix}-user-ids`}
            value={matcher.userIds}
            disabled={disabled}
            onChange={(e) => update({ userIds: e.target.value })}
          />
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Col md={3}>
          Spend Tier (PAU status)
          <br />
          <small className="text-muted">Lifetime spend, refreshed daily</small>
        </Col>
        <Col md={8}>
          <Form.Check
            type="radio"
            id={`${idPrefix}-spend-all`}
            disabled={disabled}
            checked={matcher.spendTiers.length === 0}
            onChange={() => update({ spendTiers: [] })}
            label={
              <>
                All users <small className="text-muted">no spend filter</small>
              </>
            }
          />
          {GACHA_SPEND_TIER_OPTIONS.map(({ tier, label, range }) => (
            <Form.Check
              key={tier}
              type="checkbox"
              id={`${idPrefix}-spend-${tier}`}
              disabled={disabled}
              checked={matcher.spendTiers.includes(tier)}
              onChange={() => toggleSpendTier(tier)}
              label={
                <>
                  {label} <small className="text-muted">{range}</small>
                  {audienceByTier[tier] && (
                    <small className="ml-2">{audienceByTier[tier].totalUsers.toLocaleString()} users</small>
                  )}
                </>
              }
            />
          ))}
          {allSpendTiersSelected && (
            <small className="text-muted">
              Every tier selected - this saves as <strong>All users</strong> (no spend filter), which also reaches
              payers whose tier has not been synced yet.
            </small>
          )}
          {!disabled && (
            <div>
              <Button
                variant="outline-secondary"
                size="sm"
                className="mt-2"
                disabled={audienceLoading}
                onClick={estimate}
              >
                {audienceLoading ? 'Estimating…' : 'Estimate audience'}
              </Button>
            </div>
          )}
          {audienceComputedAt && (
            <div>
              <small className="text-muted">
                Tiers computed {new Date(audienceComputedAt).toLocaleString()} - lifetime spend, refreshed daily (can be
                up to 24h behind). Counts every payer that matches this matcher&apos;s other criteria too, whenever they
                last played, and ignores the campaign&apos;s enabled flag, window and namespace. NPAU is every user
                without a tier row, so it is not counted here.
                {audienceIgnored.length > 0 && (
                  <>
                    {' '}
                    <strong>Not accounted for: {audienceIgnored.join(', ')} - the real audience is smaller.</strong>
                  </>
                )}
              </small>
            </div>
          )}
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column md={3} htmlFor={`${idPrefix}-maestro`}>
          Only Maestro
        </Form.Label>
        <Col md={9} className="d-flex align-items-center">
          <Form.Check
            type="checkbox"
            id={`${idPrefix}-maestro`}
            checked={matcher.maestroOnly}
            disabled={disabled}
            onChange={(e) => update({ maestroOnly: e.target.checked })}
          />
          <Form.Text className="text-muted mt-0">owners of the Maestro pack, any Plus/Basic tier</Form.Text>
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column md={3} htmlFor={`${idPrefix}-excluded`}>
          Excluded User Ids
          <br />
          <small className="text-muted">(separated by commas &quot;,&quot;)</small>
          <br />
          <small className="text-muted">Always wins over the criteria above</small>
        </Form.Label>
        <Col md={8}>
          <Form.Control
            as="textarea"
            id={`${idPrefix}-excluded`}
            value={matcher.excludedUserIds}
            disabled={disabled}
            onChange={(e) => update({ excludedUserIds: e.target.value })}
          />
        </Col>
      </Form.Group>
    </>
  );
}

// The filters in use, as one line.
export function describeGachaMatcher(matcher: GachaMatcherDraft): string {
  const filters = describeGachaMatcherFilters(matcher);
  return filters.length > 0 ? filters.join(' · ') : 'no filter (every player)';
}

type SectionProps = {
  title: ReactNode;
  // The filters the collapsed header reports, so a collapsed section never hides a live restriction.
  filters: string[];
  defaultExpanded: boolean;
  // "Same as campaign" (the mystery matcher): the filters are the campaign's, not this section's own.
  inherited?: boolean;
  children: ReactNode;
};

/**
 * Collapsible matcher section: react-bootstrap's Accordion with the header of Marathon's matcher (EventMatchers),
 * whose collapsible is built into that component. Expanded for a new campaign, collapsed for a saved one; the
 * collapsed header says whether any filter is set and which. Inherited ("Same as campaign"), it says so and names the
 * campaign's filters as the campaign's, so it never reads as the section's own.
 */
export function GachaMatcherSection({
  title,
  filters,
  defaultExpanded,
  inherited = false,
  children,
}: SectionProps): JSX.Element {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <Accordion defaultActiveKey={defaultExpanded ? '0' : undefined} className="mb-2">
      <Accordion.Toggle as={Card.Title} eventKey="0" onClick={() => setExpanded((value) => !value)}>
        <div
          className="border rounded px-2 py-1"
          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span className="small" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="text-muted text-uppercase">{title}</span>
            {inherited && (
              <Badge variant="info" className="ml-2">
                Same as campaign
              </Badge>
            )}
            {inherited ? (
              !expanded && (
                <span className="text-muted ml-2">
                  {filters.length > 0
                    ? `campaign matcher: ${filters.join(' · ')}`
                    : 'campaign matcher: no filter (every player)'}
                </span>
              )
            ) : (
              <>
                {filters.length > 0 ? (
                  <Badge variant="warning" className="ml-2">
                    {filters.length} filter{filters.length > 1 ? 's' : ''} applied
                  </Badge>
                ) : (
                  <Badge variant="light" className="ml-2">
                    No filter: every player
                  </Badge>
                )}
                {!expanded && filters.length > 0 && <span className="text-muted ml-2">{filters.join(' · ')}</span>}
              </>
            )}
          </span>
          <Button variant="link" size="sm">
            <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} />
          </Button>
        </div>
      </Accordion.Toggle>
      <Accordion.Collapse eventKey="0">
        <div>{children}</div>
      </Accordion.Collapse>
    </Accordion>
  );
}
